/**
 * Vault-level operations that the rest of the system calls. Each is a thin, named composition
 * of the primitives so that call sites read like the key ceremony document.
 */
import { open as aeadOpen, randomKey, seal as aeadSeal } from "./aead.js";
import { frame, utf8, wipe } from "./bytes.js";
import { decodeSealed, encodeSealed, openWith, sealTo } from "./ecies.js";
import { generateGroupKeypair } from "./group.js";
import { segmentKey } from "./kdf.js";
import { split } from "./shamir.js";
import { partialDecrypt, thresholdOpen } from "./threshold.js";
/**
 * Create a vault: generate the keypair, split the secret into shares, wipe the secret.
 * The caller must immediately seal each share to its confidant (or to the gateway's claim key
 * for pending slots) and never persist the returned shares in plaintext.
 */
export function createVault(k, n) {
    const kp = generateGroupKeypair();
    const shares = split(kp.secretKey, k, n);
    wipe(kp.secretKey);
    const mk = generateGroupKeypair();
    return {
        publicKey: kp.publicKey,
        shares,
        metadataPublicKey: mk.publicKey,
        metadataSecretKey: mk.secretKey,
        k,
        n,
    };
}
export function windowAad(vaultId, windowId) {
    return frame(utf8("tether/v1/window"), utf8(vaultId), utf8(windowId));
}
/** Generate and wrap a fresh window key to the vault public key. Returns both. */
export function newWindowKey(vaultPublicKey, vaultId, windowId) {
    const windowKey = randomKey();
    const wrapped = sealTo(vaultPublicKey, windowKey, windowAad(vaultId, windowId));
    return { windowKey, wrapped, wrappedBytes: encodeSealed(wrapped) };
}
export function segmentAad(vaultId, windowId, streamId, seq) {
    return frame(utf8("tether/v1/segment"), utf8(vaultId), utf8(windowId), utf8(streamId), utf8(String(seq)));
}
export function encryptSegment(windowKey, vaultId, windowId, streamId, seq, plaintext) {
    return aeadSeal(segmentKey(windowKey, streamId, seq), plaintext, segmentAad(vaultId, windowId, streamId, seq));
}
export function decryptSegment(windowKey, vaultId, windowId, streamId, seq, ciphertext) {
    return aeadOpen(segmentKey(windowKey, streamId, seq), ciphertext, segmentAad(vaultId, windowId, streamId, seq));
}
/** Confidant side: produce a partial for one wrapped window key. */
export function approveWindow(share, wrappedWindowKey) {
    return partialDecrypt(share, decodeSealed(wrappedWindowKey));
}
/** Requester side: recover a window key from k partials. */
export function unwrapWindowKey(vaultPublicKey, vaultId, windowId, wrappedWindowKey, partials) {
    return thresholdOpen(vaultPublicKey, decodeSealed(wrappedWindowKey), partials, windowAad(vaultId, windowId));
}
/** Pending-vault path only (before sealing): unwrap directly with the secret. */
export function unwrapWindowKeyWithSecret(vaultSecretKey, vaultPublicKey, vaultId, windowId, wrappedWindowKey) {
    return openWith(vaultSecretKey, vaultPublicKey, decodeSealed(wrappedWindowKey), windowAad(vaultId, windowId));
}
export function metadataAad(vaultId, deviceId) {
    return frame(utf8("tether/v1/meta"), utf8(vaultId), utf8(deviceId));
}
export function encryptMetadata(metadataPublicKey, vaultId, deviceId, record) {
    return encodeSealed(sealTo(metadataPublicKey, record, metadataAad(vaultId, deviceId)));
}
export function decryptMetadata(metadataSecretKey, metadataPublicKey, vaultId, deviceId, blob) {
    return openWith(metadataSecretKey, metadataPublicKey, decodeSealed(blob), metadataAad(vaultId, deviceId));
}
/** Seal any small secret (a share, the metadata secret key) to a recipient's group public key. */
export function sealSecretTo(recipientPublicKey, secret, label) {
    return encodeSealed(sealTo(recipientPublicKey, secret, utf8(label)));
}
export function openSecretWith(recipientSecretKey, recipientPublicKey, blob, label) {
    return openWith(recipientSecretKey, recipientPublicKey, decodeSealed(blob), utf8(label));
}
//# sourceMappingURL=vault.js.map